/**
 * PDF + DOCX generation client (thin).
 *
 * Backend handler: flusia-core POST /api/documents/quote.
 * Tutta la logica di template processing, rendering Puppeteer/LibreOffice e
 * sostituzione variabili è server-side. Questo file espone:
 *  - generateQuoteDocument: chiama il backend e restituisce un Blob binario
 *  - generateQuotePDF / generateQuoteDocx: legacy wrapper (backwards compat con i caller esistenti)
 *  - generateQuotePublicPDF: variante per pagine firma pubbliche (auth via signature_token)
 *  - downloadBlob: helper per triggerare il download standard browser
 *  - buildClientFileName: naming download
 *  - generateQuoteNumber: logica numerazione preventivi (business, resta client per ora)
 */
import { coreCallBlob } from './services/gatewayService';
import type { Deal } from './types';
import { supabase } from './supabase';

const CORE_URL = import.meta.env.VITE_CORE_URL || 'https://core.flusia.it';
const CORE_DEV_KEY = import.meta.env.VITE_GATEWAY_DEV_KEY as string | undefined;

export type DocumentFormat = 'pdf' | 'docx';

export interface GenerateQuoteOptions {
  signed?: boolean;
  debugMode?: boolean;
  /** Righe dell'offerta (deal_services.id) da includere; assente = tutte */
  serviceIds?: string[];
}

/**
 * Genera un documento (PDF o DOCX) dal backend flusia-core.
 * Richiede utente autenticato (JWT Supabase).
 */
export async function generateQuoteDocument(
  dealId: string,
  templateId: string,
  format: DocumentFormat,
  options: GenerateQuoteOptions = {},
): Promise<Blob> {
  return await coreCallBlob('documents/quote', {
    body: {
      dealId,
      templateId,
      format,
      signed: options.signed ?? false,
      debugMode: options.debugMode ?? false,
      ...(options.serviceIds && options.serviceIds.length > 0 ? { serviceIds: options.serviceIds } : {}),
    },
  });
}

/**
 * Legacy wrapper. I caller esistenti (`useSendProposal`, `usePDFGeneration`) usano
 * questa firma; sotto invoca il nuovo backend.
 * @deprecated Use generateQuoteDocument(dealId, templateId, 'pdf') instead.
 */
export async function generateQuotePDF(
  deal: Deal,
  _dealServices: unknown,
  _settings: unknown,
  template: { id: string },
  returnBlob = true,
  debugMode = false,
  ..._legacyFlags: unknown[]
): Promise<Blob | { output: (kind: string) => Blob }> {
  const blob = await generateQuoteDocument(deal.id, template.id, 'pdf', { debugMode });
  if (returnBlob) return blob;
  // Backwards-compat con jsPDF doc.output('blob') che restituisce il blob
  return { output: () => blob };
}

/**
 * Legacy wrapper DOCX.
 * @deprecated Use generateQuoteDocument(dealId, templateId, 'docx') instead.
 */
export async function generateQuoteDocx(
  deal: Deal,
  _dealServices: unknown,
  _settings: unknown,
  template: { id: string },
  options: { serviceIds?: string[] } = {},
): Promise<{ success: true; fileName: string; blob: Blob }> {
  const blob = await generateQuoteDocument(deal.id, template.id, 'docx', { serviceIds: options.serviceIds });
  const fileName = buildClientFileName(deal, 'docx');
  downloadBlob(blob, fileName);
  return { success: true, fileName, blob };
}

export interface GenerateQuotePublicOptions {
  /** Token pubblico (alias di `signature_requests.request_url`, dall'URL /sign/:id). */
  signatureToken: string;
  /** Se true, il PDF include la firma del cliente (richiede signatureDataUrl). */
  signed?: boolean;
  /** Data-URI base64 dell'immagine firma (canvas.toDataURL). Richiesto se signed=true. */
  signatureDataUrl?: string;
  /** Nome del firmatario (mostrato accanto alla firma nel PDF). */
  signerName?: string;
  /** Email del firmatario (audit trail). */
  signerEmail?: string;
}

/**
 * Genera un PDF preventivo via endpoint pubblico (no JWT) usato dalle pagine di
 * firma `/sign/:id` e `/signature/:id`. L'auth è basata sul `signature_token` —
 * il backend valida che la `signature_requests` row esista, sia `pending` (o
 * `completed` per ri-download) e non scaduta.
 *
 * Usa `fetch` diretto invece di `coreCallBlob` perché quel helper richiede JWT
 * Supabase, che il cliente firmatario NON ha.
 */
export async function generateQuotePublicPDF(
  options: GenerateQuotePublicOptions,
): Promise<Blob> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (CORE_DEV_KEY) headers['X-Flusia-Dev-Key'] = CORE_DEV_KEY;

  const response = await fetch(`${CORE_URL}/api/documents/quote/public`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      signatureToken: options.signatureToken,
      signed: options.signed ?? false,
      signatureDataUrl: options.signatureDataUrl,
      signerName: options.signerName,
      signerEmail: options.signerEmail,
    }),
  });

  if (!response.ok) {
    const errBody = await response.text();
    let errMsg: string;
    try {
      const parsed = JSON.parse(errBody);
      errMsg = parsed.error?.message ?? parsed.error ?? parsed.message ?? `HTTP ${response.status}`;
    } catch {
      errMsg = errBody || `HTTP ${response.status}`;
    }
    throw new Error(errMsg);
  }

  return await response.blob();
}

export function buildClientFileName(deal: Deal, format: DocumentFormat): string {
  const ext = format === 'pdf' ? '.pdf' : '.docx';
  let fileName: string;
  if (deal.quote_number) {
    fileName = `Preventivo_${deal.quote_number}`;
  } else {
    fileName = `Preventivo_${deal.title || 'Senza_Titolo'}`;
  }
  if (deal.client?.business_name) {
    const clean = deal.client.business_name.replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_');
    fileName += `_${clean}`;
  }
  const today = new Date();
  const dateStr = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
  fileName += `_${dateStr}${ext}`;
  if (fileName.length > 100) {
    fileName = fileName.substring(0, 100 - ext.length) + ext;
  }
  return fileName;
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Genera un quote number sequenziale per la prima volta che un deal viene esportato.
 * Logica business client-side per evitare race su upsert.
 * Pattern: <prefix>-<year>-<seq:3-digit-padded>
 */
export async function generateQuoteNumber(
  deal: Deal,
  settings: { quote_number_prefix?: string | null },
): Promise<string> {
  const prefix = settings.quote_number_prefix ?? 'Q';
  const year = new Date().getFullYear();

  const { data: existing } = await supabase
    .from('deals')
    .select('quote_number')
    .like('quote_number', `${prefix}-${year}-%`)
    .order('quote_number', { ascending: false })
    .limit(1);

  let nextSeq = 1;
  if (existing && existing.length > 0 && existing[0].quote_number) {
    const match = /-(\d+)$/.exec(existing[0].quote_number);
    if (match) {
      nextSeq = parseInt(match[1], 10) + 1;
    }
  }

  return `${prefix}-${year}-${String(nextSeq).padStart(3, '0')}`;
}
